import React from "react";
import { t } from "../../i18n";
import { Folder, Plus, Trash2 } from "lucide-react";

/**
 * MontageList – Grid of existing montages with open/delete actions.
 *
 * Props:
 * - montagesList: array – List of montage objects
 * - activeMontageId: string|null – Currently loaded montage ID
 * - onLoad: function(montageId) – Load a montage
 * - onDelete: function(montageId) – Delete a montage
 * - onNew: function – Reset to create a new montage
 */
export default function MontageList({ montagesList, activeMontageId, onLoad, onDelete, onNew }) {
    if (!montagesList || montagesList.length === 0) return null;

    return (
        <section className="montage-card">
            <div className="montage-list">
                <div className="montage-list-header">
                    <h3>
                        <Folder size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
                        {t("montage.list.title")} ({montagesList.length})
                    </h3>
                    {activeMontageId && (
                        <button onClick={onNew} className="montage-btn-small montage-btn-outline">
                            <Plus size={14} />
                            {t("montage.list.new")}
                        </button>
                    )}
                </div>
                <div className="montage-list-grid">
                    {montagesList.map(m => {
                        const isActive = m.montage_id === activeMontageId;
                        const segCount = m.json_data?.segments?.length || 0;
                        const status = m.status || "draft";

                        return (
                            <div
                                key={m.montage_id}
                                className={`montage-list-item ${isActive ? "active" : ""}`}
                            >
                                <div className="montage-list-item-info">
                                    <strong>{m.title}</strong>
                                    <span className="montage-list-item-meta">
                                        {m.aspect_ratio} &middot; {m.resolution}
                                        {segCount > 0 && ` \u00b7 ${segCount} seg.`}
                                    </span>
                                    <span className={`montage-status-badge ${status}`}>
                                        {status === "completed" && `\u2705 ${t("montage.status.completed")}`}
                                        {status === "exporting" && `\u23f3 ${t("montage.status.exporting")}`}
                                        {status === "export_failed" && `\u274c ${t("montage.status.error")}`}
                                        {status === "draft" && `\u270f ${t("montage.status.draft")}`}
                                    </span>
                                </div>
                                <div className="montage-list-item-actions">
                                    {!isActive && (
                                        <button
                                            onClick={() => onLoad(m.montage_id)}
                                            className="montage-btn-small montage-btn-primary"
                                        >
                                            {t("montage.list.open")}
                                        </button>
                                    )}
                                    {isActive && (
                                        <span className="montage-active-badge">&check; {t("montage.list.opened")}</span>
                                    )}
                                    <button
                                        onClick={() => onDelete(m.montage_id)}
                                        className="montage-btn-small montage-btn-danger"
                                        title={t("common.delete")}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
